/**
 * 🏛️ AdminHierarchyPage — Organizational Hierarchy & Data Scope
 *
 * Enterprise org chart: departments (tree) + user hierarchy (visual org chart).
 * Defines who reports to whom, department assignments, and role_type.
 * This page is the SSOT for data visibility (scope) in the system.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Building2, ChevronUp, ChevronDown, Users, Plus, UserPlus,
  User, Eye, X, Save, AlertTriangle, Network, CheckSquare, Square,
  ArrowRightLeft, BarChart3, Pencil,
} from 'lucide-react';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { ModuleCard, ModuleLoadingState } from '../../shared/components/ModulePageHeader';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import {
  getMembers,
  addMember,
  updateHierarchy,
  getDepartments,
  createDepartment,
  getRoles,
  getAvailableProfiles,
  validateOrgMember,
  isMemberStructureComplete,
  requiresDirectManager,
  ROLE_TYPE_OPTIONS,
  ROLE_TYPE_LABELS,
  SCOPE_DESCRIPTIONS,
  type OrgMember,
  type OrgDepartment,
  type RoleType,
} from '../../../services/organizationService';

// ─── Data fetching (delegated to organizationService) ───────

// ─── Cycle detection ────────────────────────────────────────

function detectCycle(
  members: OrgMember[],
  userId: string,
  newReportsTo: string | null,
): boolean {
  if (!newReportsTo) return false;
  if (newReportsTo === userId) return true;

  const visited = new Set<string>();
  let current: string | null = newReportsTo;

  while (current) {
    if (current === userId) return true;
    if (visited.has(current)) return false;
    visited.add(current);
    const member = members.find(m => m.user_id === current);
    current = member?.reports_to_user_id ?? null;
  }

  return false;
}

// ─── Department Tree Component ──────────────────────────────

const DepartmentTree: React.FC<{
  departments: OrgDepartment[];
  selectedDeptId: string | null;
  onSelect: (id: string | null) => void;
  orgId: string;
}> = ({ departments, selectedDeptId, onSelect, orgId }) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['all']));
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const qc = useQueryClient();

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const createMutation = useMutation({
    mutationFn: () => createDepartment(orgId, newName),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-departments', orgId] });
      setCreating(false);
      setNewName('');
    },
  });

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-[var(--border-default)]">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">Departamentos</span>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          title="Novo departamento"
        >
          <Plus size={12} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-1">
        {/* All / No filter */}
        <button
          type="button"
          onClick={() => onSelect(null)}
          className={`w-full flex items-center gap-1.5 px-2 py-1 text-left text-[11px] transition-colors rounded-sm mx-0.5 ${
            selectedDeptId === null
              ? 'bg-[var(--bg-elevated)] text-[var(--text-primary)] font-semibold'
              : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]'
          }`}
        >
          <Building2 size={11} />
          Todos
          <span className="ml-auto text-[9px] text-[var(--text-muted)]">
            {departments.length}
          </span>
        </button>

        {departments.map(dept => (
          <button
            key={dept.id}
            type="button"
            onClick={() => onSelect(dept.id)}
            className={`w-full flex items-center gap-1.5 px-2 py-1 text-left text-[11px] transition-colors rounded-sm mx-0.5 ${
              selectedDeptId === dept.id
                ? 'bg-[var(--bg-elevated)] text-[var(--text-primary)] font-semibold'
                : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]'
            }`}
          >
            <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: dept.color }} />
            <span className="truncate">{dept.name}</span>
          </button>
        ))}

        {creating && (
          <div className="px-2 py-1.5 flex gap-1">
            <input
              className="enterprise-input text-[11px] flex-1 px-1.5 py-0.5"
              placeholder="Nome do departamento"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && newName.trim()) createMutation.mutate();
                if (e.key === 'Escape') { setCreating(false); setNewName(''); }
              }}
              autoFocus
            />
            <button
              type="button"
              onClick={() => { if (newName.trim()) createMutation.mutate(); }}
              disabled={!newName.trim() || createMutation.isPending}
              className="enterprise-btn-primary text-[9px] px-1.5 py-0.5"
            >
              OK
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Org Node Styling ───────────────────────────────────────

const ROLE_COLORS = {
  director:    { border: 'border-emerald-500/60', bg: 'bg-emerald-500/[0.04]', badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300', dot: 'bg-emerald-500' },
  manager:     { border: 'border-amber-500/60', bg: 'bg-amber-500/[0.04]', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300', dot: 'bg-amber-500' },
  lead:        { border: 'border-blue-500/60', bg: 'bg-blue-500/[0.04]', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300', dot: 'bg-blue-500' },
  operational: { border: 'border-[var(--border-default)]', bg: 'bg-[var(--bg-surface)]', badge: 'bg-slate-100 text-slate-600 dark:bg-zinc-800 dark:text-zinc-400', dot: 'bg-slate-400' },
} as const;

const LINE_COLORS: Record<RoleType, string> = {
  director:    'border-emerald-400/60',
  manager:     'border-amber-400/60',
  lead:        'border-blue-400/60',
  operational: 'border-slate-300 dark:border-zinc-600',
};

const ROLE_ORDER: RoleType[] = ['operational', 'lead', 'manager', 'director'];

function promoteRole(current: RoleType): RoleType | null {
  const idx = ROLE_ORDER.indexOf(current);
  return idx < ROLE_ORDER.length - 1 ? ROLE_ORDER[idx + 1] : null;
}

function demoteRole(current: RoleType): RoleType | null {
  const idx = ROLE_ORDER.indexOf(current);
  return idx > 0 ? ROLE_ORDER[idx - 1] : null;
}

// ─── Org Node Card ──────────────────────────────────────────

const OrgNodeCard: React.FC<{
  member: OrgMember;
  onClick: () => void;
  isSelected: boolean;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
  isDescendantHighlighted?: boolean;
  isAncestorHighlighted?: boolean;
  deptColor?: string;
  onPromote?: () => void;
  onDemote?: () => void;
  isBatchSelected?: boolean;
  onToggleBatch?: () => void;
}> = React.memo(({ member, onClick, isSelected, onHoverStart, onHoverEnd, isDescendantHighlighted, isAncestorHighlighted, deptColor, onPromote, onDemote, isBatchSelected, onToggleBatch }) => {
  const colors = ROLE_COLORS[member.role_type];
  const isHighlighted = isDescendantHighlighted || isAncestorHighlighted;

  // Heatmap: compute load level based on active tasks
  const heatLevel = member.active_task_count <= 0 ? 'idle' : member.active_task_count <= 5 ? 'normal' : member.active_task_count <= 12 ? 'busy' : 'overloaded';
  const heatIndicator = heatLevel === 'idle' ? 'bg-slate-300' : heatLevel === 'normal' ? 'bg-emerald-400' : heatLevel === 'busy' ? 'bg-amber-400' : 'bg-red-400';

  // Conditional border: incomplete structure = yellow, late tasks = red, overloaded manager = orange
  const isIncomplete = !isMemberStructureComplete(member);
  const alertBorder = isIncomplete
    ? 'border-yellow-500/70'
    : member.late_task_count > 0
    ? 'border-red-500/70'
    : member.subordinate_count > 10
    ? 'border-orange-500/70'
    : colors.border;

  return (
    <div className="relative group" onMouseEnter={onHoverStart} onMouseLeave={onHoverEnd}>
      {/* Hover action bar — top-right corner */}
      <div className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity z-20 flex items-center gap-0.5 bg-[var(--bg-elevated)] border border-[var(--border-default)] rounded-md px-1 py-0.5 shadow-lg">
        {onPromote && (
          <button type="button" onClick={e => { e.stopPropagation(); onPromote(); }} className="p-0.5 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-[var(--text-muted)] hover:text-emerald-600 transition-colors" title="Promover">
            <ChevronUp size={10} />
          </button>
        )}
        {onDemote && (
          <button type="button" onClick={e => { e.stopPropagation(); onDemote(); }} className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-[var(--text-muted)] hover:text-red-600 transition-colors" title="Rebaixar">
            <ChevronDown size={10} />
          </button>
        )}
        <button type="button" onClick={e => { e.stopPropagation(); onClick(); }} className="p-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-[var(--text-muted)] hover:text-blue-600 transition-colors" title="Editar">
          <Pencil size={9} />
        </button>
      </div>

      <button
        type="button"
        onClick={onClick}
        className={`
          relative text-left border-[1.5px] rounded-md transition-all duration-150 cursor-pointer w-[190px]
          ${alertBorder} ${colors.bg}
          ${isSelected ? 'ring-2 ring-[var(--accent-primary)] shadow-lg scale-[1.02]' : ''}
          ${isDescendantHighlighted ? 'ring-1 ring-[var(--accent-primary)]/50 shadow-md' : ''}
          ${isAncestorHighlighted ? 'ring-1 ring-amber-400/50 shadow-md' : ''}
          ${isBatchSelected ? 'ring-2 ring-sky-500 shadow-md' : ''}
          ${!isHighlighted && !isSelected && !isBatchSelected ? 'hover:shadow-md hover:scale-[1.01]' : ''}
          px-3 py-2.5
        `}
      >
        {/* Department color stripe */}
        {deptColor && (
          <div className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full" style={{ backgroundColor: deptColor }} />
        )}

        {/* Batch select checkbox */}
        {onToggleBatch && (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onToggleBatch(); }}
            className="absolute top-1 left-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] z-10"
          >
            {isBatchSelected ? <CheckSquare size={11} className="text-sky-500" /> : <Square size={11} />}
          </button>
        )}

        {/* Header row */}
        <div className="flex items-center gap-2">
          <div className="relative w-7 h-7 rounded-full bg-[var(--bg-elevated)] border border-[var(--border-default)] flex items-center justify-center shrink-0">
            <User size={12} className="text-[var(--text-muted)]" />
            {/* Heat status dot */}
            <span className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white dark:border-zinc-900 ${heatIndicator}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold text-[var(--text-primary)] truncate leading-tight">
              {member.user_name}
            </div>
            {member.role_name && (
              <div className="text-[9px] text-[var(--text-secondary)] truncate leading-tight mt-0.5">
                {member.role_name}
              </div>
            )}
            <span className={`inline-block text-[7px] font-bold uppercase tracking-wider px-1.5 py-[1px] rounded-sm mt-0.5 ${colors.badge}`}>
              {ROLE_TYPE_LABELS[member.role_type]}
            </span>
          </div>
        </div>

        {/* Meta row */}
        <div className="flex items-center gap-1.5 mt-2 text-[8px] text-[var(--text-muted)]">
          {isIncomplete && (
            <span className="flex items-center gap-0.5 text-yellow-600 font-semibold">
              <AlertTriangle size={8} /> Incompleto
            </span>
          )}
          {member.department_name && (
            <span className="flex items-center gap-0.5 truncate">
              <Building2 size={8} />
              <span className="truncate">{member.department_name}</span>
            </span>
          )}
          <span className="flex items-center gap-3 ml-auto shrink-0">
            {member.subordinate_count > 0 && (
              <span className="flex items-center gap-0.5 text-[var(--text-secondary)]">
                <Users size={8} /> {member.subordinate_count}
              </span>
            )}
            {member.active_task_count > 0 && (
              <span className="flex items-center gap-0.5 text-[var(--text-secondary)]">
                <BarChart3 size={8} /> {member.active_task_count}
              </span>
            )}
            {member.late_task_count > 0 && (
              <span className="flex items-center gap-0.5 text-red-500 font-semibold">
                <AlertTriangle size={8} /> {member.late_task_count}
              </span>
            )}
          </span>
        </div>
      </button>
    </div>
  );
});

// ─── Tree Utilities ─────────────────────────────────────────

interface TreeNode { member: OrgMember; children: TreeNode[]; }

function buildTree(members: OrgMember[], filterDeptId: string | null): TreeNode[] {
  // When filtering by dept, also include users without department (incomplete)
  const filtered = filterDeptId
    ? members.filter(m => m.department_id === filterDeptId || !m.department_id)
    : members;
  const allIds = new Set(filtered.map(m => m.user_id));
  const order: Record<RoleType, number> = { director: 0, manager: 1, lead: 2, operational: 3 };
  const nodeMap = new Map<string, TreeNode>();
  let roots: TreeNode[] = [];

  for (const m of filtered) nodeMap.set(m.user_id, { member: m, children: [] });
  for (const m of filtered) {
    const node = nodeMap.get(m.user_id)!;
    const parent = m.reports_to_user_id && allIds.has(m.reports_to_user_id) ? nodeMap.get(m.reports_to_user_id) : null;
    parent ? parent.children.push(node) : roots.push(node);
  }

  // Fallback: if no roots found, render all as roots
  if (roots.length === 0 && filtered.length > 0) {
    roots = Array.from(nodeMap.values());
  }

  const sortRec = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => (order[a.member.role_type] ?? 9) - (order[b.member.role_type] ?? 9));
    nodes.forEach(n => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

function getDescendantIds(node: TreeNode): Set<string> {
  const ids = new Set<string>();
  const walk = (n: TreeNode) => { ids.add(n.member.user_id); n.children.forEach(walk); };
  walk(node);
  return ids;
}

function buildParentMap(roots: TreeNode[]): Map<string, string> {
  const map = new Map<string, string>();
  const walk = (nodes: TreeNode[]) => {
    for (const n of nodes) {
      for (const c of n.children) map.set(c.member.user_id, n.member.user_id);
      walk(n.children);
    }
  };
  walk(roots);
  return map;
}

function getAncestorIds(userId: string, parentMap: Map<string, string>): Set<string> {
  const ids = new Set<string>();
  let cur = parentMap.get(userId);
  while (cur) { ids.add(cur); cur = parentMap.get(cur); }
  return ids;
}

// ─── Subtree Renderer ───────────────────────────────────────

const SubTree: React.FC<{
  node: TreeNode;
  selectedUserId: string | null;
  onSelectUser: (id: string) => void;
  descendantIds: Set<string>;
  ancestorIds: Set<string>;
  onHoverNode: (node: TreeNode | null) => void;
  deptColorMap: Map<string, string>;
  onPromote: (userId: string) => void;
  onDemote: (userId: string) => void;
  batchSelectedIds: Set<string>;
  onToggleBatch?: (userId: string) => void;
}> = React.memo(({ node, selectedUserId, onSelectUser, descendantIds, ancestorIds, onHoverNode, deptColorMap, onPromote, onDemote, batchSelectedIds, onToggleBatch }) => {
  const hasChildren = node.children.length > 0;
  const lineClass = LINE_COLORS[node.member.role_type];
  const canPromote = promoteRole(node.member.role_type) !== null;
  const canDemote = demoteRole(node.member.role_type) !== null;

  return (
    <div className="flex flex-col items-center">
      <OrgNodeCard
        member={node.member}
        isSelected={selectedUserId === node.member.user_id}
        onClick={() => onSelectUser(node.member.user_id)}
        onHoverStart={() => onHoverNode(node)}
        onHoverEnd={() => onHoverNode(null)}
        isDescendantHighlighted={descendantIds.has(node.member.user_id)}
        isAncestorHighlighted={ancestorIds.has(node.member.user_id)}
        deptColor={deptColorMap.get(node.member.department_id ?? '')}
        onPromote={canPromote ? () => onPromote(node.member.user_id) : undefined}
        onDemote={canDemote ? () => onDemote(node.member.user_id) : undefined}
        isBatchSelected={batchSelectedIds.has(node.member.user_id)}
        onToggleBatch={onToggleBatch ? () => onToggleBatch(node.member.user_id) : undefined}
      />

      {hasChildren && (
        <>
          {/* Vertical connector down */}
          <div className={`w-0 h-7 border-l-[2px] ${lineClass}`} />

          {/* Children row with horizontal connector */}
          <div className="flex items-start">
            {node.children.map((child, i) => (
              <div key={child.member.user_id} className="flex flex-col items-center px-3">
                {/* Horizontal connector segments */}
                {node.children.length > 1 && (
                  <div className="flex w-full h-0">
                    <div className={`flex-1 ${i > 0 ? `border-t-[2px] ${lineClass}` : ''}`} />
                    <div className={`flex-1 ${i < node.children.length - 1 ? `border-t-[2px] ${lineClass}` : ''}`} />
                  </div>
                )}
                {/* Vertical tick to child */}
                <div className={`w-0 h-5 border-l-[2px] ${lineClass}`} />
                <SubTree
                  node={child}
                  selectedUserId={selectedUserId}
                  onSelectUser={onSelectUser}
                  descendantIds={descendantIds}
                  ancestorIds={ancestorIds}
                  onHoverNode={onHoverNode}
                  deptColorMap={deptColorMap}
                  onPromote={onPromote}
                  onDemote={onDemote}
                  batchSelectedIds={batchSelectedIds}
                  onToggleBatch={onToggleBatch}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
});

// ─── Org Chart ──────────────────────────────────────────────

const EMPTY_SET = new Set<string>();

const OrgChart: React.FC<{
  members: OrgMember[];
  selectedUserId: string | null;
  onSelectUser: (id: string) => void;
  filterDeptId: string | null;
  departments: OrgDepartment[];
  onPromote: (userId: string) => void;
  onDemote: (userId: string) => void;
  batchSelectedIds: Set<string>;
  onToggleBatch?: (userId: string) => void;
}> = ({ members, selectedUserId, onSelectUser, filterDeptId, departments, onPromote, onDemote, batchSelectedIds, onToggleBatch }) => {
  const [hoveredNode, setHoveredNode] = useState<TreeNode | null>(null);
  const [zoom, setZoom] = useState(1);

  const roots = useMemo(() => buildTree(members, filterDeptId), [members, filterDeptId]);
  const parentMap = useMemo(() => buildParentMap(roots), [roots]);

  const deptColorMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of departments) if (d.color) map.set(d.id, d.color);
    return map;
  }, [departments]);

  const descendantIds = useMemo(() => {
    if (!hoveredNode) return EMPTY_SET;
    return getDescendantIds(hoveredNode);
  }, [hoveredNode]);

  const ancestorIds = useMemo(() => {
    if (!hoveredNode) return EMPTY_SET;
    return getAncestorIds(hoveredNode.member.user_id, parentMap);
  }, [hoveredNode, parentMap]);

  if (members.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-[var(--text-muted)]">
        <div className="text-center max-w-[280px]">
          <Network size={32} className="mx-auto mb-3 opacity-20" />
          <div className="text-[12px] font-medium text-[var(--text-secondary)] mb-1">Hierarquia vazia</div>
          <div className="text-[10px] leading-relaxed">
            Adicione membros à organização para estruturar a hierarquia e definir visibilidades.
          </div>
        </div>
      </div>
    );
  }

  if (roots.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-[var(--text-muted)]">
        <div className="text-center max-w-[240px]">
          <Building2 size={24} className="mx-auto mb-2 opacity-20" />
          <div className="text-[11px]">Nenhum membro neste departamento</div>
          <div className="text-[9px] mt-0.5">Selecione outro departamento ou utilize "Todos"</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col relative">
      {/* Scrollable org chart canvas */}
      <div className="flex-1 overflow-auto">
        <div className="flex justify-center py-8 px-6 min-w-fit transition-transform origin-top-left" style={{ transform: `scale(${zoom})` }}>
          <div className="max-w-[1100px] w-full flex justify-center">
            <div className="flex gap-8">
              {roots.map(root => (
                <SubTree
                  key={root.member.user_id}
                  node={root}
                  selectedUserId={selectedUserId}
                  onSelectUser={onSelectUser}
                  descendantIds={descendantIds}
                  ancestorIds={ancestorIds}
                  onHoverNode={setHoveredNode}
                  deptColorMap={deptColorMap}
                  onPromote={onPromote}
                  onDemote={onDemote}
                  batchSelectedIds={batchSelectedIds}
                  onToggleBatch={onToggleBatch}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating Zoom Controls */}
      <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-[var(--bg-surface)]/80 backdrop-blur-sm border border-[var(--border-default)] rounded-md shadow-lg px-1 py-0.5 z-20">
        <button
          type="button"
          onClick={() => setZoom(z => Math.max(0.25, z - 0.1))}
          className="p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          title="Zoom out"
        >
          <ChevronDown size={14} />
        </button>
        <span className="text-[10px] font-mono text-[var(--text-secondary)] min-w-[36px] text-center select-none">
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          onClick={() => setZoom(z => Math.min(2, z + 0.1))}
          className="p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          title="Zoom in"
        >
          <ChevronUp size={14} />
        </button>
        {zoom !== 1 && (
          <button
            type="button"
            onClick={() => setZoom(1)}
            className="p-1 text-[10px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            title="Resetar zoom"
          >
            1:1
          </button>
        )}
      </div>
    </div>
  );
};

// ─── User Detail Sidebar ────────────────────────────────────

function countLevelsBelow(members: OrgMember[], userId: string): number {
  const childrenMap = new Map<string, string[]>();
  for (const m of members) {
    if (m.reports_to_user_id) {
      const arr = childrenMap.get(m.reports_to_user_id) ?? [];
      arr.push(m.user_id);
      childrenMap.set(m.reports_to_user_id, arr);
    }
  }
  let maxDepth = 0;
  const walk = (id: string, depth: number) => {
    if (depth > maxDepth) maxDepth = depth;
    for (const cid of (childrenMap.get(id) ?? [])) walk(cid, depth + 1);
  };
  walk(userId, 0);
  return maxDepth;
}

function countTotalSubordinates(members: OrgMember[], userId: string): number {
  const childrenMap = new Map<string, string[]>();
  for (const m of members) {
    if (m.reports_to_user_id) {
      const arr = childrenMap.get(m.reports_to_user_id) ?? [];
      arr.push(m.user_id);
      childrenMap.set(m.reports_to_user_id, arr);
    }
  }
  let count = 0;
  const walk = (id: string) => {
    for (const cid of (childrenMap.get(id) ?? [])) { count++; walk(cid); }
  };
  walk(userId);
  return count;
}

const UserDetailSidebar: React.FC<{
  member: OrgMember;
  allMembers: OrgMember[];
  departments: OrgDepartment[];
  orgId: string;
  onClose: () => void;
}> = ({ member, allMembers, departments, orgId, onClose }) => {
  const qc = useQueryClient();
  const [draft, setDraft] = useState({
    role_type: member.role_type,
    role_id: member.role_id ?? '',
    department_id: member.department_id ?? '',
    reports_to_user_id: member.reports_to_user_id ?? '',
  });
  const [cycleError, setCycleError] = useState(false);

  // Fetch available workflow roles for this org
  const { data: workflowRoles = [] } = useQuery({
    queryKey: ['workflow-roles', orgId],
    queryFn: () => getRoles(orgId),
    enabled: !!orgId,
    staleTime: 60_000,
  });

  const hasChanges = (
    draft.role_type !== member.role_type ||
    (draft.role_id || null) !== (member.role_id || null) ||
    (draft.department_id || null) !== (member.department_id || null) ||
    (draft.reports_to_user_id || null) !== (member.reports_to_user_id || null)
  );

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        role_type: draft.role_type,
        role_id: draft.role_id || null,
        department_id: draft.department_id || null,
        reports_to_user_id: draft.reports_to_user_id || null,
      };
      const check = validateOrgMember(payload);
      if (!check.isComplete) {
        throw new Error(`Estrutura incompleta: ${check.missing.join(', ')}`);
      }
      return updateHierarchy(orgId, member.user_id, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-hierarchy-members', orgId] });
      qc.invalidateQueries({ queryKey: ['admin-users-table', orgId] });
      onClose();
    },
  });

  const handleReportsToChange = (value: string) => {
    const isCycle = detectCycle(allMembers, member.user_id, value || null);
    setCycleError(isCycle);
    if (!isCycle) {
      setDraft(prev => ({ ...prev, reports_to_user_id: value }));
    }
  };

  // Potential managers: anyone except self and own subordinates
  const possibleManagers = allMembers.filter(m =>
    m.user_id !== member.user_id &&
    !detectCycle(allMembers, m.user_id, member.user_id),
  );

  return (
    <div className="w-[320px] shrink-0 border-l border-[var(--border-default)] bg-[var(--bg-surface)] flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border-default)]">
        <span className="text-[11px] font-bold text-[var(--text-primary)]">Detalhes do Membro</span>
        <button type="button" onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
          <X size={13} />
        </button>
      </div>

      {/* User info */}
      <div className="px-3 py-2 border-b border-[var(--border-default)]">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[var(--bg-elevated)] flex items-center justify-center">
            <User size={14} className="text-[var(--text-muted)]" />
          </div>
          <div>
            <div className="text-[12px] font-semibold text-[var(--text-primary)]">{member.user_name}</div>
            {(() => {
              const cpfMatch = member.user_email?.match(/^cpf_(\d{11})@internal-workflow\.com$/);
              if (cpfMatch) {
                const d = cpfMatch[1];
                return <div className="text-[10px] font-mono text-amber-600 dark:text-amber-400">{`\u{1F464} CPF: ${d.slice(0,3)}.${d.slice(3,6)}.${d.slice(6,9)}-${d.slice(9,11)}`}</div>;
              }
              return <div className="text-[10px] text-[var(--text-muted)]">{member.user_email}</div>;
            })()}
          </div>
        </div>
        {/* Completeness checklist */}
        {(() => {
          const v = validateOrgMember({
            department_id: draft.department_id || null,
            role_id: draft.role_id || null,
            role_type: draft.role_type,
            reports_to_user_id: draft.reports_to_user_id || null,
          });
          if (v.isComplete) return null;
          return (
            <div className="mt-2 p-2 rounded bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300/50 text-[9px]">
              <div className="font-bold text-yellow-700 dark:text-yellow-400 mb-1 flex items-center gap-1">
                <AlertTriangle size={9} /> Estrutura incompleta
              </div>
              {v.missing.map(m => (
                <div key={m} className="text-yellow-600 dark:text-yellow-500">• {m}</div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-3">
        {/* Role Type */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">
            Nível Hierárquico
          </label>
          <select
            className="enterprise-input text-[11px]"
            value={draft.role_type}
            onChange={e => setDraft(prev => ({ ...prev, role_type: e.target.value as RoleType }))}
          >
            {ROLE_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <div className="flex items-center gap-1 mt-0.5">
            <Eye size={9} className="text-[var(--text-muted)]" />
            <span className="text-[9px] text-[var(--text-muted)]">
              {SCOPE_DESCRIPTIONS[draft.role_type]}
            </span>
          </div>
        </div>

        {/* Job Title (role_id) */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">
            Cargo
          </label>
          <select
            className="enterprise-input text-[11px]"
            value={draft.role_id}
            onChange={e => setDraft(prev => ({ ...prev, role_id: e.target.value }))}
          >
            <option value="">Nenhum</option>
            {workflowRoles.map((r: { id: string; name: string }) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>

        {/* Department */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">
            Departamento
          </label>
          <select
            className="enterprise-input text-[11px]"
            value={draft.department_id}
            onChange={e => setDraft(prev => ({ ...prev, department_id: e.target.value }))}
          >
            <option value="">Nenhum</option>
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>
        </div>

        {/* Reports To */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">
            Gestor Direto
          </label>
          <select
            className={`enterprise-input text-[11px] ${requiresDirectManager(draft.role_type) && !draft.reports_to_user_id ? 'border-yellow-500/50' : ''}`}
            value={draft.reports_to_user_id}
            onChange={e => handleReportsToChange(e.target.value)}
          >
            <option value="">
              {requiresDirectManager(draft.role_type) ? 'Selecionar gestor...' : 'Nenhum (topo da hierarquia)'}
            </option>
            {possibleManagers.map(m => (
              <option key={m.user_id} value={m.user_id}>
                {m.user_name} ({ROLE_TYPE_LABELS[m.role_type]})
              </option>
            ))}
          </select>
          {cycleError && (
            <div className="flex items-center gap-1 text-[9px] text-red-500 mt-0.5">
              <AlertTriangle size={9} /> Ciclo detectado — não é possível vincular
            </div>
          )}
        </div>

        {/* Scope & Operational Detail */}
        <div className="enterprise-card-surface p-2.5 rounded border border-[var(--border-default)] mt-2 flex flex-col gap-2">
          <div className="text-[9px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">
            Escopo de Visibilidade
          </div>
          <div className="text-[10px] text-[var(--text-primary)]">
            {SCOPE_DESCRIPTIONS[draft.role_type]}
          </div>
          <div className="grid grid-cols-3 gap-2 mt-1">
            <div className="text-center p-1.5 rounded bg-[var(--bg-elevated)]">
              <div className="text-[13px] font-bold tabular-nums text-[var(--text-primary)]">{member.subordinate_count}</div>
              <div className="text-[7px] uppercase tracking-wider text-[var(--text-muted)]">Diretos</div>
            </div>
            <div className="text-center p-1.5 rounded bg-[var(--bg-elevated)]">
              <div className="text-[13px] font-bold tabular-nums text-[var(--text-primary)]">{countTotalSubordinates(allMembers, member.user_id)}</div>
              <div className="text-[7px] uppercase tracking-wider text-[var(--text-muted)]">Total Sub.</div>
            </div>
            <div className="text-center p-1.5 rounded bg-[var(--bg-elevated)]">
              <div className="text-[13px] font-bold tabular-nums text-[var(--text-primary)]">{countLevelsBelow(allMembers, member.user_id)}</div>
              <div className="text-[7px] uppercase tracking-wider text-[var(--text-muted)]">Níveis</div>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-1 pt-1.5 border-t border-[var(--border-subtle)]">
            <BarChart3 size={10} className="text-[var(--text-muted)]" />
            <span className="text-[10px] text-[var(--text-secondary)]">
              {member.active_task_count} tarefa{member.active_task_count !== 1 ? 's' : ''} ativa{member.active_task_count !== 1 ? 's' : ''}
            </span>
          </div>
          {member.late_task_count > 0 && (
            <div className="flex items-center gap-2 mt-1">
              <AlertTriangle size={10} className="text-red-500" />
              <span className="text-[10px] text-red-500 font-medium">
                {member.late_task_count} atrasada{member.late_task_count !== 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-[var(--border-default)] flex justify-end gap-2">
        <button type="button" onClick={onClose} className="enterprise-btn-ghost text-[11px] px-3 py-1">
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={!hasChanges || cycleError || saveMutation.isPending}
          className="enterprise-btn-primary text-[11px] px-3 py-1 flex items-center gap-1"
        >
          <Save size={10} /> {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </div>
  );
};



// ─── Hierarchy Summary Bar ──────────────────────────────────

const HierarchySummaryBar: React.FC<{ members: OrgMember[]; departments: OrgDepartment[] }> = ({ members, departments }) => {
  const stats = useMemo(() => {
    const usersWithoutManager = members.filter(m => !m.reports_to_user_id && m.role_type !== 'director');
    const usersWithoutDepartment = members.filter(m => !m.department_id);
    const usersWithoutRole = members.filter(m => !m.role_id);
    const incompleteUsers = members.filter(m => !isMemberStructureComplete(m));
    const overloadedManagers = members.filter(m => m.subordinate_count > 10);
    return {
      totalUsers: members.length,
      totalDepartments: departments.length,
      usersWithoutManager: usersWithoutManager.length,
      usersWithoutDepartment: usersWithoutDepartment.length,
      usersWithoutRole: usersWithoutRole.length,
      incompleteUsers: incompleteUsers.length,
      overloadedManagers: overloadedManagers.length,
    };
  }, [members, departments]);

  return (
    <div className="flex items-center gap-4 px-3 py-1.5 border-b border-[var(--border-default)] bg-[var(--bg-surface)]/50 text-[10px]">
      <span className="flex items-center gap-1.5 text-[var(--text-secondary)]">
        <Users size={10} />
        <span className="font-semibold tabular-nums">{stats.totalUsers}</span> pessoas
      </span>
      <span className="flex items-center gap-1.5 text-[var(--text-secondary)]">
        <Building2 size={10} />
        <span className="font-semibold tabular-nums">{stats.totalDepartments}</span> departamentos
      </span>
      {stats.usersWithoutManager > 0 && (
        <span className="flex items-center gap-1 text-amber-500 font-medium">
          <AlertTriangle size={9} />
          <span className="tabular-nums">{stats.usersWithoutManager}</span> sem gestor
        </span>
      )}
      {stats.usersWithoutDepartment > 0 && (
        <span className="flex items-center gap-1 text-amber-500 font-medium">
          <Building2 size={9} />
          <span className="tabular-nums">{stats.usersWithoutDepartment}</span> sem departamento
        </span>
      )}
      {stats.usersWithoutRole > 0 && (
        <span className="flex items-center gap-1 text-amber-500 font-medium">
          <AlertTriangle size={9} />
          <span className="tabular-nums">{stats.usersWithoutRole}</span> sem cargo
        </span>
      )}
      {stats.incompleteUsers > 0 && (
        <span className="flex items-center gap-1 text-yellow-600 font-semibold">
          <AlertTriangle size={9} />
          <span className="tabular-nums">{stats.incompleteUsers}</span> precisam de configuração
        </span>
      )}
      {stats.overloadedManagers > 0 && (
        <span className="flex items-center gap-1 text-red-500 font-medium">
          <AlertTriangle size={9} />
          <span className="tabular-nums">{stats.overloadedManagers}</span> sobrecarregado{stats.overloadedManagers > 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
};

// ─── Add Member Modal ───────────────────────────────────────

const AddMemberModal: React.FC<{
  orgId: string;
  existingUserIds: string[];
  members: OrgMember[];
  departments: OrgDepartment[];
  onClose: () => void;
}> = ({ orgId, existingUserIds, members, departments, onClose }) => {
  const qc = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [roleType, setRoleType] = useState<RoleType>('operational');
  const [roleId, setRoleId] = useState('');
  const [deptId, setDeptId] = useState('');
  const [managerId, setManagerId] = useState('');

  // Fetch all profiles to pick from
  const { data: allProfiles = [] } = useQuery({
    queryKey: ['all-profiles-for-add', orgId],
    queryFn: () => getAvailableProfiles(existingUserIds),
    enabled: !!orgId,
  });

  // Fetch workflow roles
  const { data: workflowRoles = [] } = useQuery({
    queryKey: ['workflow-roles', orgId],
    queryFn: () => getRoles(orgId),
    enabled: !!orgId,
    staleTime: 60_000,
  });

  const availableProfiles = allProfiles;

  const validation = validateOrgMember({
    department_id: deptId || null,
    role_id: roleId || null,
    role_type: roleType,
    reports_to_user_id: managerId || null,
  });

  const addMutation = useMutation({
    mutationFn: () => addMember({
      organization_id: orgId,
      user_id: selectedUserId,
      role_type: roleType,
      role_id: roleId || null,
      department_id: deptId || null,
      reports_to_user_id: managerId || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-hierarchy-members', orgId] });
      qc.invalidateQueries({ queryKey: ['admin-users-table', orgId] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-[var(--bg-surface)] border border-[var(--border-default)] rounded shadow-2xl w-[380px] max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-default)]">
          <span className="text-[12px] font-bold text-[var(--text-primary)]">Adicionar Membro</span>
          <button type="button" onClick={onClose} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"><X size={14} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3">
          {/* User select */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">Usuário</label>
            <select className="enterprise-input text-[11px]" value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}>
              <option value="">Selecionar usuário...</option>
              {availableProfiles.map(p => (
                <option key={p.id} value={p.id}>{p.full_name ?? p.email}</option>
              ))}
            </select>
            {availableProfiles.length === 0 && (
              <span className="text-[9px] text-[var(--text-muted)]">Todos os usuários já são membros</span>
            )}
          </div>

          {/* Role Type */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">Nível Hierárquico</label>
            <select className="enterprise-input text-[11px]" value={roleType} onChange={e => setRoleType(e.target.value as RoleType)}>
              {ROLE_TYPE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>

          {/* Job Title */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">Cargo <span className="text-red-400">*</span></label>
            <select className={`enterprise-input text-[11px] ${!roleId ? 'border-yellow-500/50' : ''}`} value={roleId} onChange={e => setRoleId(e.target.value)}>
              <option value="">Selecionar cargo...</option>
              {workflowRoles.map((r: { id: string; name: string }) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>

          {/* Department */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">Departamento <span className="text-red-400">*</span></label>
            <select className={`enterprise-input text-[11px] ${!deptId ? 'border-yellow-500/50' : ''}`} value={deptId} onChange={e => setDeptId(e.target.value)}>
              <option value="">Selecionar departamento...</option>
              {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          {/* Manager */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">Gestor Direto {roleType !== 'director' && <span className="text-red-400">*</span>}</label>
            <select className={`enterprise-input text-[11px] ${roleType !== 'director' && !managerId ? 'border-yellow-500/50' : ''}`} value={managerId} onChange={e => setManagerId(e.target.value)}>
              <option value="">{roleType === 'director' ? 'Nenhum (topo da hierarquia)' : 'Selecionar gestor...'}</option>
              {members.map(m => <option key={m.user_id} value={m.user_id}>{m.user_name} ({ROLE_TYPE_LABELS[m.role_type]})</option>)}
            </select>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-[var(--border-default)] flex justify-end gap-2">
          <button type="button" onClick={onClose} className="enterprise-btn-ghost text-[11px] px-3 py-1.5">Cancelar</button>
          <button
            type="button"
            onClick={() => addMutation.mutate()}
            disabled={!selectedUserId || !validation.isComplete || addMutation.isPending}
            className="enterprise-btn-primary text-[11px] px-3 py-1.5 flex items-center gap-1"
          >
            <UserPlus size={11} /> {addMutation.isPending ? 'Adicionando...' : 'Adicionar'}
          </button>
        </div>

        {addMutation.isError && (
          <div className="px-4 py-2 text-[10px] text-red-500 border-t border-red-200">
            Erro: {(addMutation.error as Error)?.message ?? 'Falha ao adicionar'}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Page ──────────────────────────────────────────────

interface AdminHierarchyPageProps {
  externalShowAddModal?: boolean;
  onCloseAddModal?: () => void;
  externalToggleBatch?: boolean;
  onBatchDone?: () => void;
}

export const AdminHierarchyPage: React.FC<AdminHierarchyPageProps> = ({
  externalShowAddModal,
  onCloseAddModal,
  externalToggleBatch,
  onBatchDone,
}) => {
  const { workspace } = useWorkspace();
  const orgId = workspace?.organizationId ?? '';
  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [batchSelectedIds, setBatchSelectedIds] = useState<Set<string>>(new Set());
  const [batchDept, setBatchDept] = useState('');
  const [batchManager, setBatchManager] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [batchMode, setBatchMode] = useState(false);

  // External triggers
  React.useEffect(() => {
    if (externalShowAddModal) setShowAddModal(true);
  }, [externalShowAddModal]);

  React.useEffect(() => {
    if (externalToggleBatch !== undefined) {
      setBatchMode(externalToggleBatch);
      if (!externalToggleBatch) { setBatchSelectedIds(new Set()); setBatchDept(''); setBatchManager(''); }
    }
  }, [externalToggleBatch]);

  const deptsQuery = useQuery({
    queryKey: ['org-departments', orgId],
    queryFn: () => getDepartments(orgId),
    enabled: !!orgId,
  });

  const membersQuery = useQuery({
    queryKey: ['org-hierarchy-members', orgId],
    queryFn: () => getMembers(orgId),
    enabled: !!orgId,
  });

  const departments = deptsQuery.data ?? [];
  const members = membersQuery.data ?? [];
  const selectedMember = selectedUserId ? members.find(m => m.user_id === selectedUserId) ?? null : null;

  const isLoading = deptsQuery.isPending || membersQuery.isPending;

  const qc = useQueryClient();
  const roleMutation = useMutation({
    mutationFn: ({ userId, newRole }: { userId: string; newRole: RoleType }) =>
      updateHierarchy(orgId, userId, { role_type: newRole }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-hierarchy-members', orgId] }),
  });

  const batchMutation = useMutation({
    mutationFn: async (updates: { department_id?: string | null; reports_to_user_id?: string | null }) => {
      const promises = Array.from(batchSelectedIds).map(uid =>
        updateHierarchy(orgId, uid, updates),
      );
      await Promise.all(promises);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-hierarchy-members', orgId] });
      setBatchSelectedIds(new Set());
      setBatchDept('');
      setBatchManager('');
    },
  });

  const handlePromote = useCallback((userId: string) => {
    const m = members.find(x => x.user_id === userId);
    if (!m) return;
    const next = promoteRole(m.role_type);
    if (next) roleMutation.mutate({ userId, newRole: next });
  }, [members, roleMutation]);

  const handleDemote = useCallback((userId: string) => {
    const m = members.find(x => x.user_id === userId);
    if (!m) return;
    const next = demoteRole(m.role_type);
    if (next) roleMutation.mutate({ userId, newRole: next });
  }, [members, roleMutation]);

  const handleToggleBatch = useCallback((userId: string) => {
    setBatchSelectedIds(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  }, []);

  const hasBatchSelection = batchSelectedIds.size > 0;

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
        <div className="enterprise-page h-full flex flex-col overflow-hidden">

          {/* Batch selection toolbar — only visible when items selected */}
          {hasBatchSelection && (
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-[var(--border-default)] bg-[var(--bg-surface)]/60">
              <span className="text-[9px] text-[var(--text-secondary)] font-semibold">
                {batchSelectedIds.size} selecionado{batchSelectedIds.size > 1 ? 's' : ''}
              </span>
              <select
                className="enterprise-input text-[10px] px-1.5 py-0.5 w-[140px]"
                value={batchDept}
                onChange={e => setBatchDept(e.target.value)}
              >
                <option value="">Mover p/ depto...</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
              {batchDept && (
                <button
                  type="button"
                  onClick={() => batchMutation.mutate({ department_id: batchDept || null })}
                  disabled={batchMutation.isPending}
                  className="enterprise-btn-primary text-[9px] px-2 py-0.5"
                >
                  Aplicar
                </button>
              )}
              <select
                className="enterprise-input text-[10px] px-1.5 py-0.5 w-[160px]"
                value={batchManager}
                onChange={e => setBatchManager(e.target.value)}
              >
                <option value="">Alterar gestor...</option>
                {members.filter(m => !batchSelectedIds.has(m.user_id)).map(m => (
                  <option key={m.user_id} value={m.user_id}>{m.user_name}</option>
                ))}
              </select>
              {batchManager && (
                <button
                  type="button"
                  onClick={() => batchMutation.mutate({ reports_to_user_id: batchManager || null })}
                  disabled={batchMutation.isPending}
                  className="enterprise-btn-primary text-[9px] px-2 py-0.5"
                >
                  Aplicar
                </button>
              )}
              <button
                type="button"
                onClick={() => setBatchSelectedIds(new Set())}
                className="enterprise-btn-ghost text-[9px] px-1.5 py-0.5 ml-auto"
              >
                Limpar seleção
              </button>
            </div>
          )}

          {isLoading ? (
            <ModuleLoadingState label="Carregando hierarquia" />
          ) : (
            <div className="flex-1 flex min-h-0">
              {/* Left: Department Tree */}
              <div className="w-[160px] shrink-0 bg-[var(--officebar-bg)]" style={{ borderRight: '1px solid var(--shell-divider)', boxShadow: '2px 0 6px rgba(0,0,0,0.15)' }}>
                <DepartmentTree
                  departments={departments}
                  selectedDeptId={selectedDeptId}
                  onSelect={setSelectedDeptId}
                  orgId={orgId}
                />
              </div>

              {/* Center: Org Chart */}
              <div className="flex-1 flex flex-col min-w-0 bg-[var(--canvas-bg)]">
                <HierarchySummaryBar members={members} departments={departments} />
                <div className="flex-1 min-h-0">
                  <OrgChart
                    members={members}
                    selectedUserId={selectedUserId}
                    onSelectUser={setSelectedUserId}
                    filterDeptId={selectedDeptId}
                    departments={departments}
                    onPromote={handlePromote}
                    onDemote={handleDemote}
                    batchSelectedIds={batchSelectedIds}
                    onToggleBatch={batchMode ? handleToggleBatch : undefined}
                  />
                </div>
              </div>

              {/* Right: User Detail Sidebar */}
              {selectedMember && (
                <UserDetailSidebar
                  member={selectedMember}
                  allMembers={members}
                  departments={departments}
                  orgId={orgId}
                  onClose={() => setSelectedUserId(null)}
                />
              )}
            </div>
          )}
        </div>

      {showAddModal && (
        <AddMemberModal
          orgId={orgId}
          existingUserIds={members.map(m => m.user_id)}
          members={members}
          departments={departments}
          onClose={() => { setShowAddModal(false); onCloseAddModal?.(); }}
        />
      )}
    </RequireCapability>
  );
};
